import { useTranslation } from 'react-i18next'
import { UploadFile } from 'antd'
import axios from 'axios'
import dayjs from 'dayjs'
import { useRecoilState } from 'recoil'
import {
  buildingFilesState,
  revalidateFileListState,
  uploadingFiles
} from 'store/buildingStore'
import {
  BuildingFile,
  CustomUploadFile,
  ListBuildingFiles,
  SelectedFiles,
  UploadingFiles,
  UploadStatus
} from 'types/building'
import Utils from 'utils'

import useDirectory from './useDirectory'
import useMutation from './useMutation'

type Props = {
  buildingId: string | number
  category: number
  folderId?: number
  existingFiles?: BuildingFile[]
  allBuildingFiles?: ListBuildingFiles
}

export default function useUpload({
  buildingId,
  category,
  folderId,
  existingFiles: propExistingFiles,
  allBuildingFiles
}: Props) {
  const { t } = useTranslation()
  const [selectedFiles, setSelectedFiles] =
    useRecoilState<SelectedFiles[]>(uploadingFiles)
  const [buildingFiles, setBuildingFiles] = useRecoilState(buildingFilesState)
  const [, setRevalidateFileList] = useRecoilState(revalidateFileListState)
  const { navigate } = useDirectory()

  const { trigger: getPresignedUrl } = useMutation(
    'v1/buildings/presigned-urls'
  )

  const { trigger: saveFile } = useMutation('v1/buildings/files')

  const { trigger: deleteFile, isMutating } = useMutation('v1/buildings/files')

  const convertedFile = (uploadedFile: UploadFile, path: string) => {
    return {
      extension: uploadedFile.name.split('.').pop(),
      name: uploadedFile.name,
      mimeType: uploadedFile.type,
      size: uploadedFile.size,
      s3Path: path,
      takenAt: dayjs(uploadedFile.lastModified).format('YYYY-MM-DDTHH:mm:ssZ')
    }
  }

  const changeStatusState = (
    status: UploadStatus,
    uploadFile: SelectedFiles,
    index?: number,
    dataId?: number
  ) => {
    if (index || index === 0) {
      setSelectedFiles((prev) =>
        prev.map((item) =>
          item.id === uploadFile.id
            ? {
                ...item,
                files: item.files.map((subitem) =>
                  subitem.file.uid === uploadFile.files[index].file.uid
                    ? {
                        ...subitem,
                        uploadStatus: status,
                        id: dataId || 0
                      }
                    : subitem
                )
              }
            : item
        )
      )
    } else {
      setSelectedFiles((prev) =>
        prev.map((item) =>
          item.id === uploadFile.id
            ? {
                ...item,
                files: item.files.map((subitem) => ({
                  ...subitem,
                  uploadStatus: status
                }))
              }
            : item
        )
      )
    }
  }

  const uploadFile = async (
    duplicateOption?: number,
    duplicates?: { newFile: CustomUploadFile; oldFile: BuildingFile }[],
    filesBatch?: SelectedFiles
  ) => {
    let newUploadFiles = filesBatch || selectedFiles[selectedFiles.length - 1]

    if (!newUploadFiles || !newUploadFiles.files.length) return

    const buildingLevelFiles = allBuildingFiles?.files?.files || []
    const existingFiles = [
      ...(propExistingFiles || buildingFiles.files || []),
      ...buildingLevelFiles
    ]

    // duplicateOption 1: Replace (Overwrite)
    if (duplicateOption === 1 && duplicates?.length) {
      // 1. Only get the last file with the same name in the batch to overwrite (Spec 2)
      const duplicateOriginalNames = duplicates.map(
        (d) => d.newFile.originalName
      )

      // Filter the list in the batch
      const filteredFiles = newUploadFiles.files
        .filter((fItem, idx) => {
          const f = fItem.file
          const isInternalDuplicateOfTarget = duplicateOriginalNames.includes(
            f.originalName
          )

          const hasLaterSameOriginal = newUploadFiles.files
            .slice(idx + 1)
            .some((later) => later.file.originalName === f.originalName)

          if (isInternalDuplicateOfTarget && hasLaterSameOriginal) {
            return false // Remove older file in the batch
          }
          return true
        })
        .map((fItem) => {
          const match = duplicates?.find(
            (d) => d.newFile.originalName === fItem.file.originalName
          )
          return match
            ? {
                ...fItem,
                overwriteID: match.oldFile.id,
                mode: 'overwrite' as const
              }
            : fItem
        })

      newUploadFiles = { ...newUploadFiles, files: filteredFiles }

      // Update state to include overwriteID
      setSelectedFiles((prev) =>
        prev.map((item) =>
          item.id === newUploadFiles.id ? newUploadFiles : item
        )
      )

      // NOTE: No longer manually delete old files, but will send overwriteID during the saveFile step
    }

    // duplicateOption 2: Keep Both
    if (duplicateOption === 2) {
      const sessionSuccessFileNames = selectedFiles
        .filter(
          (batch) =>
            batch.buildingId === `${buildingId}` && batch.category === category
        )
        .flatMap((batch) => batch.files)
        .filter((item) => item.uploadStatus === UploadStatus.SUCCESS)
        .map((item) => item.file.name)
      // Numbering (1), (2)... based on all building files (Spec 2)
      const serverFileNames = existingFiles.map((f: BuildingFile) => f.name)
      const allExistingNames = [...serverFileNames, ...sessionSuccessFileNames]
      const currentBatchFullNames: string[] = []

      const indexedFiles = newUploadFiles.files.map((fItem) => {
        const f = fItem.file
        const base = f.originalName || f.name
        const lastDotIndex = base.lastIndexOf('.')
        const extension =
          lastDotIndex !== -1 ? base.substring(lastDotIndex) : ''
        const baseName =
          lastDotIndex !== -1 ? base.substring(0, lastDotIndex) : base

        let finalName = f.name
        let counter = 1

        // Match with server and files already processed in this batch
        while (
          allExistingNames.includes(finalName) ||
          currentBatchFullNames.includes(finalName)
        ) {
          finalName = `${baseName}(${counter})${extension}`
          counter += 1
        }

        currentBatchFullNames.push(finalName)

        // Fix: Create a new File object because File.name is read-only
        const renamedFile = new File(
          [fItem.file as unknown as BlobPart],
          finalName,
          {
            type: fItem.file.type,
            lastModified: fItem.file.lastModified
          }
        ) as unknown as CustomUploadFile
        renamedFile.uid = fItem.file.uid
        renamedFile.originalName = fItem.file.originalName

        const match = duplicates?.find(
          (d) => d.newFile.originalName === fItem.file.originalName
        )

        return {
          ...fItem,
          file: renamedFile,
          mode: match ? ('keep' as const) : undefined,
          overwriteID: match ? match.oldFile.id : undefined
        }
      })

      newUploadFiles = { ...newUploadFiles, files: indexedFiles }
      setSelectedFiles((prev) =>
        prev.map((item) =>
          item.id === newUploadFiles.id ? newUploadFiles : item
        )
      )
    }

    if (!newUploadFiles.files.length) return

    await getPresignedUrl(
      {
        data: {
          buildingId: Number(buildingId),
          count: newUploadFiles.files.length,
          queueId: newUploadFiles.id,
          category
        }
      },
      {
        onSuccess: async (data) => {
          const paths = data.data.s3PreSignLinks

          /* eslint-disable no-await-in-loop */
          for (let i = 0; i < paths.length; i += 1) {
            const currentFileUpload = newUploadFiles.files[i]
            await axios
              .put(paths[i].presignedUrl, currentFileUpload.file, {
                signal: currentFileUpload.abortSignal?.signal
              })
              .then(async () => {
                const files = convertedFile(
                  currentFileUpload.file,
                  paths[i].key
                )

                await saveFile({
                  data: {
                    buildingId: Number(buildingId),
                    category,
                    folderId,
                    overwriteID: currentFileUpload.overwriteID,
                    mode: currentFileUpload.mode,
                    ...files
                  }
                })
                  .then((data) => {
                    changeStatusState(
                      UploadStatus.SUCCESS,
                      newUploadFiles,
                      i,
                      data.data.id
                    )
                    // Optimistic UI: immediately add/update the file in
                    // buildingFilesState with a local blob URL as thumbnail
                    // so the user sees the image without waiting for the
                    // server to finish generating the real thumbnail.
                    const originFile =
                      currentFileUpload.file.originFileObj ||
                      currentFileUpload.file
                    if (originFile) {
                      const blobUrl = URL.createObjectURL(originFile as any)
                      const optimisticFile: BuildingFile = {
                        id: data.data.id,
                        name: currentFileUpload.file.name,
                        thumbnail: blobUrl,
                        s3Path: paths[i].key,
                        mimeType: currentFileUpload.file.type || '',
                        extension:
                          currentFileUpload.file.name.split('.').pop() || '',
                        size: currentFileUpload.file.size,
                        createdAt: new Date().toISOString(),
                        pointId: null,
                        takenAt: null
                      }
                      if (currentFileUpload.overwriteID) {
                        // Overwrite: replace existing file entry with the new optimistic file
                        setBuildingFiles((prev) => ({
                          ...prev,
                          files: prev.files.map((f) =>
                            f.id === currentFileUpload.overwriteID
                              ? optimisticFile
                              : f
                          )
                        }))
                      } else {
                        // New file: add optimistically
                        setBuildingFiles((prev) => ({
                          ...prev,
                          files: [...prev.files, optimisticFile]
                        }))
                      }
                    }
                  })
                  .catch(() => {
                    changeStatusState(UploadStatus.FAIL, newUploadFiles, i)
                  })
              })
              .catch(() => {
                changeStatusState(UploadStatus.FAIL, newUploadFiles, i)
              })
            if (i === paths.length - 1) {
              setRevalidateFileList(folderId || category)
            }
          }
        },
        onError: (error) => {
          changeStatusState(UploadStatus.FAIL, newUploadFiles)
          Utils.handleErrorNavigate(error, navigate)
        }
      }
    )
  }

  const reUpload = (
    uploadId: number,
    file: CustomUploadFile,
    overwriteID?: number,
    mode?: 'overwrite' | 'keep'
  ) => {
    const newUploadFiles = selectedFiles.find((item) => item.id === uploadId)
    if (newUploadFiles) {
      const index = newUploadFiles.files.findIndex(
        (item) => item.file.uid === file.uid
      )
      changeStatusState(UploadStatus.LOADING, newUploadFiles, index)
      getPresignedUrl(
        {
          data: {
            buildingId: Number(buildingId),
            count: 1,
            queueId: 1,
            category
          }
        },
        {
          onSuccess: async (data) => {
            const paths = data.data.s3PreSignLinks
            await axios
              .put(paths[0].presignedUrl, file, {
                signal: newUploadFiles.files.find(
                  (item) => item.file.uid === file.uid
                )?.abortSignal?.signal
              })
              .then(async () => {
                const files = convertedFile(file, paths[0].key)
                await saveFile({
                  data: {
                    buildingId: Number(buildingId),
                    category,
                    folderId,
                    overwriteID,
                    mode,
                    ...files
                  }
                })
                  .then((data) => {
                    changeStatusState(
                      UploadStatus.SUCCESS,
                      newUploadFiles,
                      index,
                      data.data.id
                    )
                    // Optimistic UI for re-upload: update blob thumbnail
                    const originFile = file.originFileObj || file
                    if (originFile) {
                      const blobUrl = URL.createObjectURL(originFile as any)
                      setBuildingFiles((prev) => ({
                        ...prev,
                        files: prev.files.map((f) =>
                          f.id === data.data.id
                            ? { ...f, thumbnail: blobUrl }
                            : f
                        )
                      }))
                    }
                    setRevalidateFileList(folderId || category)
                  })
                  .catch(() => {
                    changeStatusState(UploadStatus.FAIL, newUploadFiles, index)
                  })
              })
              .catch(() => {
                changeStatusState(UploadStatus.FAIL, newUploadFiles, index)
              })
          },
          onError: (error) => {
            changeStatusState(UploadStatus.FAIL, newUploadFiles, index)
            Utils.handleErrorNavigate(error, navigate)
          }
        }
      )
    }
  }

  const cancelUpload = (selectedId: number, file: UploadingFiles) => () => {
    if (file.uploadStatus === UploadStatus.LOADING) {
      if (file.abortSignal) {
        file.abortSignal.abort()

        setSelectedFiles((prev) =>
          prev
            .map((item) =>
              item.id === selectedId
                ? {
                    ...item,
                    files: item.files.filter(
                      (subitem) => subitem.file.uid !== file.file.uid
                    )
                  }
                : item
            )
            .filter((item) => item.files.length)
        )
      }
    }

    if (file.uploadStatus === UploadStatus.SUCCESS) {
      if (file.id && !isMutating) {
        deleteFile(
          {
            method: 'delete',
            data: null,
            query: [file.id],
            successMessage: t('organization.successDeletedNotification')
          },
          {
            onSuccess: () => {
              setBuildingFiles((prev) => ({
                ...prev,
                files: prev.files.filter((item) => item.id !== file.id)
              }))
              setSelectedFiles((prev) =>
                prev
                  .map((item) =>
                    item.id === selectedId
                      ? {
                          ...item,
                          files: item.files.filter(
                            (subitem) => subitem.id !== file.id
                          )
                        }
                      : item
                  )
                  .filter((item) => item.files.length)
              )
            },
            onError: (error) => {
              setRevalidateFileList(category)
              Utils.handleErrorNavigate(error, navigate)
            }
          }
        )
      }
    }

    if (file.uploadStatus === UploadStatus.FAIL) {
      setSelectedFiles((prev) =>
        prev
          .map((item) =>
            item.id === selectedId
              ? {
                  ...item,
                  files: item.files.filter(
                    (subitem) => subitem.file.uid !== file.file.uid
                  )
                }
              : item
          )
          .filter((item) => item.files.length)
      )
    }
  }

  const removeSuccessFile = () => {
    setSelectedFiles((prev) =>
      prev
        .filter((item) =>
          item.files.find((file) => file.uploadStatus !== UploadStatus.SUCCESS)
        )
        .map((item) => ({
          ...item,
          files: item.files.filter(
            (file) => file.uploadStatus !== UploadStatus.SUCCESS
          )
        }))
    )
  }

  return { uploadFile, reUpload, cancelUpload, removeSuccessFile }
}
